import cx from 'classnames';
import Image, { ImageProps } from 'next/image';
import { ReactElement } from 'react';

import { IconButton } from '../form/button/IconButton';
import { IconArrowLeft } from '../icons/ArrowLeft';
import { IconCross } from '../icons/Cross';
import { Typography } from '../typography/Typography';

import { ModalSize } from './ModalSkeleton';

import style from './ModalImageHeader.module.css';

export type ModalImageHeaderPropsBase = {
    onClose?: () => void;
    onPreviousButtonClick?: () => void;
    size?: ModalSize;
    title?: string | ReactElement;
};

// Variant pour le fit des images
type ImageCommonProps = Pick<ImageProps, 'src' | 'alt' | 'loader' | 'unoptimized'>;
export type CoverImageProps = {
    fit?: 'cover';
    image: ImageCommonProps;
};
export type ContainImageProps = {
    fit: 'contain';
    image: ImageCommonProps & Partial<Pick<ImageProps, 'width' | 'height'>>;
};

export type ModalImageHeaderProps = ModalImageHeaderPropsBase &
    (CoverImageProps | ContainImageProps);
export function isContainImage(
    props: ModalImageHeaderProps,
): props is ModalImageHeaderPropsBase & ContainImageProps {
    return props.fit === 'contain';
}

export function ModalImageHeader(props: ModalImageHeaderProps) {
    const { image, fit = 'cover', onClose, onPreviousButtonClick, size, title } = props;
    return (
        <div
            className={cx(style.header, {
                [style.big]: size !== 'small',
                [style.objectFitContain]: fit === 'contain',
            })}
        >
            <Image
                {...image}
                // Keep the image sizes in sync with the ModalSkeleton.module.css (--modal-small-width, --modal-large-width)
                sizes="(min-width: 960px) 960px, (min-width: 640px) 640px, 100vw"
                alt={image.alt}
                className={cx(style.image, {
                    [style.big]: size !== 'small',
                    [style.objectFitContain]: fit === 'contain',
                    [style.objectFitCover]: fit === 'cover',
                })}
                loading="eager"
                fill={fit === 'cover'}
                width={isContainImage(props) ? (props.image.width ?? 960) : undefined}
                height={isContainImage(props) ? (props.image.height ?? 960) : undefined}
                priority
            />
            {title ? (
                <Typography as="h3" scale="secondary-xs-bold" className={style.title}>
                    {title}
                </Typography>
            ) : null}
            {onClose ? (
                <IconButton onClick={onClose} Icon={IconCross} className={style.closeButton} />
            ) : null}
            {onPreviousButtonClick ? (
                <IconButton
                    onClick={onPreviousButtonClick}
                    Icon={IconArrowLeft}
                    className={style.previousButton}
                />
            ) : null}
        </div>
    );
}
